
import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Bot, Send, User, Lightbulb, Target, TrendingUp } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

interface ChatMessage {
  id: string;
  type: 'user' | 'ai';
  content: string;
  timestamp: string;
}

interface CareerInsight {
  icon: React.ElementType;
  title: string;
  content: string;
  category: 'skill' | 'opportunity' | 'growth';
}

const AICareerCoach: React.FC = () => {
  const { toast } = useToast();
  const [messages, setMessages] = useState<ChatMessage[]>([
    {
      id: '1',
      type: 'ai',
      content: "Hi! I'm your AI Career Coach. I'm here to help you navigate your career journey. What would you like to discuss today?",
      timestamp: new Date().toLocaleTimeString()
    }
  ]);
  const [inputMessage, setInputMessage] = useState('');

  const careerInsights: CareerInsight[] = [
    {
      icon: Lightbulb,
      title: 'Skill Recommendation',
      content: 'Based on your interests in tech, consider learning Python programming. It\'s highly demanded in AI and data science.',
      category: 'skill'
    },
    {
      icon: Target,
      title: 'Career Path Suggestion',
      content: 'Your profile suggests a good fit for Product Management. Consider taking courses in user experience and market analysis.',
      category: 'opportunity'
    },
    {
      icon: TrendingUp,
      title: 'Growth Opportunity',
      content: 'The renewable energy sector is growing rapidly. Your interest in sustainability could lead to exciting career opportunities.',
      category: 'growth'
    }
  ];

  const predefinedQuestions = [
    "What skills should I focus on for my career?",
    "How do I prepare for job interviews?",
    "What career paths match my interests?",
    "How can I build a strong professional network?"
  ];

  const sendMessage = () => {
    if (!inputMessage.trim()) return;

    const userMessage: ChatMessage = {
      id: Date.now().toString(),
      type: 'user',
      content: inputMessage,
      timestamp: new Date().toLocaleTimeString()
    };

    // Simulate AI response
    const aiResponses = [
      "That's a great question! Based on current industry trends, I'd recommend focusing on digital skills like data analysis, programming, and digital marketing. These are in high demand across many industries.",
      "For interview preparation, practice the STAR method (Situation, Task, Action, Result) for behavioral questions. Also, research the company thoroughly and prepare thoughtful questions to ask the interviewer.",
      "Given your interests and skills, I see potential in fields like UX/UI design, product management, or data science. Each offers exciting growth opportunities and aligns with current market demands.",
      "Building a professional network starts with being genuine and helpful. Attend industry events, engage on LinkedIn, and don't be afraid to reach out to professionals for informational interviews."
    ];

    const aiMessage: ChatMessage = {
      id: (Date.now() + 1).toString(),
      type: 'ai',
      content: aiResponses[Math.floor(Math.random() * aiResponses.length)],
      timestamp: new Date().toLocaleTimeString()
    };

    setMessages([...messages, userMessage, aiMessage]);
    setInputMessage('');
    
    toast({
      title: "AI Coach Response",
      description: "Your AI career coach has provided personalized advice!"
    });
  };

  const askPredefinedQuestion = (question: string) => {
    setInputMessage(question);
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Bot className="h-5 w-5 text-green-500" />
            AI-Powered Career Coach
          </CardTitle>
          <CardDescription>
            Get personalized career guidance powered by artificial intelligence.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Career Insights */}
          <div>
            <h3 className="font-semibold mb-3">Personalized Career Insights</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {careerInsights.map((insight, index) => (
                <div key={index} className={`border rounded-lg p-4 ${
                  insight.category === 'skill' ? 'bg-blue-50 border-blue-200' :
                  insight.category === 'opportunity' ? 'bg-green-50 border-green-200' :
                  'bg-purple-50 border-purple-200'
                }`}>
                  <div className="flex items-start gap-3">
                    <insight.icon className={`h-5 w-5 mt-1 ${
                      insight.category === 'skill' ? 'text-blue-500' :
                      insight.category === 'opportunity' ? 'text-green-500' :
                      'text-purple-500'
                    }`} />
                    <div>
                      <h4 className="font-medium text-sm">{insight.title}</h4>
                      <p className="text-xs text-muted-foreground mt-1">{insight.content}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Chat Interface */}
          <div className="border rounded-lg">
            <div className="h-80 overflow-y-auto p-4 space-y-4">
              {messages.map((message) => (
                <div key={message.id} className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-xs lg:max-w-md px-4 py-2 rounded-lg ${
                    message.type === 'user' 
                      ? 'bg-blue-500 text-white' 
                      : 'bg-gray-100 text-gray-800'
                  }`}>
                    <div className="flex items-center gap-2 mb-1">
                      {message.type === 'user' ? (
                        <User className="h-4 w-4" />
                      ) : (
                        <Bot className="h-4 w-4" />
                      )}
                      <span className="text-xs opacity-75">{message.timestamp}</span>
                    </div>
                    <p className="text-sm">{message.content}</p>
                  </div>
                </div>
              ))}
            </div>
            
            {/* Quick Questions */}
            <div className="border-t p-3">
              <p className="text-sm font-medium mb-2">Quick Questions:</p>
              <div className="flex flex-wrap gap-2">
                {predefinedQuestions.map((question, index) => (
                  <Button
                    key={index}
                    size="sm"
                    variant="outline"
                    className="text-xs"
                    onClick={() => askPredefinedQuestion(question)}
                  >
                    {question}
                  </Button>
                ))}
              </div>
            </div>
            
            {/* Message Input */}
            <div className="border-t p-4">
              <div className="flex gap-2">
                <Input
                  value={inputMessage}
                  onChange={(e) => setInputMessage(e.target.value)}
                  placeholder="Ask your AI career coach anything..."
                  onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
                />
                <Button onClick={sendMessage} className="bg-green-500 hover:bg-green-600">
                  <Send className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AICareerCoach;
